import { useMemo } from "react";
import { ThemeProvider as AuthMuiThemeProvider } from "@mui/material/styles";
import { Box } from "@mui/material";
import { useTheme as useAppTheme } from "../../context/ThemeContext";
import { getAppBrand } from "../../config/resolve-app-brand";
import { getWhiteLabelKey } from "../../config/white-label";
import {
  createRegisterPageDarkTheme,
  createRegisterPageLightTheme,
} from "../../theme/registerPageTheme";
import AuthLeftVisual from "./AuthLeftVisual";
import { getLoginHeroUrl } from "./authHeroUrls";

import "../../styles/register-exacc.css";

export default function AuthExaccLayout({
  children,
  heroUrl,
  showLeftVisual = true,
  contentMaxWidth = 460,
  stickyLeftVisual = false,
}) {
  const { isDarkMode } = useAppTheme();
  const appBrand = useMemo(() => getAppBrand(), []);
  const whiteLabelKey = useMemo(() => getWhiteLabelKey(), []);
  const resolvedHeroUrl = heroUrl ?? getLoginHeroUrl(isDarkMode);
  const theme = useMemo(
    () =>
      isDarkMode
        ? createRegisterPageDarkTheme(appBrand)
        : createRegisterPageLightTheme(appBrand),
    [appBrand, isDarkMode]
  );

  return (
    <AuthMuiThemeProvider theme={theme}>
      <Box
        className="register-exacc-scope"
        data-theme={isDarkMode ? "dark" : "light"}
        data-white-label={whiteLabelKey}
        sx={{
          minHeight: "100dvh",
          width: "100%",
          position: "relative",
          color: "text.primary",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="exacc-bg">
          <div className="exacc-bg__gradient" />
          <div className="exacc-bg__sweep" />
          <div className="exacc-bg__orb exacc-bg__orb--1" />
          <div className="exacc-bg__orb exacc-bg__orb--2" />
        </div>

        <Box
          sx={{
            flex: 1,
            position: "relative",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: showLeftVisual ? "1fr 1fr" : "1fr" },
            alignItems: "start",
            justifyItems: "stretch",
            alignContent: "start",
            maxWidth: 1100,
            mx: "auto",
            px: { xs: 2, sm: 4, md: 3 },
            pt: { xs: 4, md: 10 },
            pb: { xs: 4, md: 6 },
            columnGap: { md: 4, lg: 5 },
            zIndex: 1,
            boxSizing: "border-box",
          }}
        >
          {showLeftVisual && (
            <Box
              sx={{
                display: { xs: "none", md: stickyLeftVisual ? "flex" : "block" },
                flexDirection: stickyLeftVisual ? "column" : undefined,
                position: stickyLeftVisual ? "sticky" : "relative",
                top: stickyLeftVisual ? { md: 40, lg: 48 } : undefined,
                alignSelf: "start",
                width: "100%",
                maxWidth: 460,
                ml: "auto",
                minHeight: stickyLeftVisual ? { md: "calc(100vh - 7rem)" } : undefined,
                maxHeight: stickyLeftVisual ? { md: "calc(100vh - 7rem)" } : undefined,
                zIndex: 2,
              }}
            >
              <AuthLeftVisual heroUrl={resolvedHeroUrl} stretch={stickyLeftVisual} />
            </Box>
          )}

          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: { xs: "center", md: showLeftVisual ? "flex-start" : "center" },
              gridColumn: showLeftVisual ? undefined : { xs: "1", md: "1 / -1" },
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: contentMaxWidth,
                mr: showLeftVisual ? "auto" : undefined,
                mx: showLeftVisual ? undefined : "auto",
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>
      </Box>
    </AuthMuiThemeProvider>
  );
}
